// superResProtocol.ts
// ラスタータイル リアルタイム超解像プロトコル
//
// z > maxzoom のとき、低ズームタイルを切り出し＋拡大＋アンシャープマスクで擬似高解像度化する。
// z <= maxzoom のときはパススルー（オーバーヘッドなし）。
//
// URL形式:
//   XYZ:     superres://{z}/{x}/{y}.jpg?url=https://.../{z}/{x}/{y}.jpg&maxzoom=18
//   PMTiles: superres://{z}/{x}/{y}.png?pmtiles=https://...pmtiles&maxzoom=15
//
// blendModeProtocol.js と同じアーキテクチャ（addProtocol + OffscreenCanvas）。

import { calculateTileCoords, drawImageWithCrop } from './blendModeProtocol'
import { PMTiles } from 'pmtiles'

const TILE_SIZE = 256

/**
 * 超解像フィルタ
 *
 * 1. アンシャープマスク — エッジをくっきり
 * 2. 軽いコントラスト補正 — メリハリを出す
 * 3. ほんのり彩度補正 — 色褪せ防止
 */
function applySuperResFilter(
  ctx: OffscreenCanvasRenderingContext2D,
  width: number,
  height: number,
  strength: number
): void {
  const imageData = ctx.getImageData(0, 0, width, height)
  const src = imageData.data
  const len = src.length

  // --- ステップ1: アンシャープマスク ---
  const blurred = new Uint8ClampedArray(len)
  boxBlur3x3(src, blurred, width, height)
  const blurred2 = new Uint8ClampedArray(len)
  boxBlur3x3(blurred, blurred2, width, height)

  const sharpAmount = strength * 2.2
  for (let i = 0; i < len; i += 4) {
    src[i] = clamp(src[i] + sharpAmount * (src[i] - blurred2[i]))
    src[i + 1] = clamp(src[i + 1] + sharpAmount * (src[i + 1] - blurred2[i + 1]))
    src[i + 2] = clamp(src[i + 2] + sharpAmount * (src[i + 2] - blurred2[i + 2]))
  }

  // --- ステップ2: 軽いコントラスト補正 ---
  const contrast = 0.15 * strength
  for (let i = 0; i < len; i += 4) {
    src[i] = clamp(128 + (src[i] - 128) * (1 + contrast))
    src[i + 1] = clamp(128 + (src[i + 1] - 128) * (1 + contrast))
    src[i + 2] = clamp(128 + (src[i + 2] - 128) * (1 + contrast))
  }

  // --- ステップ3: ほんのり彩度補正 ---
  const satBoost = 1 + strength * 0.15
  for (let i = 0; i < len; i += 4) {
    const gray = 0.299 * src[i] + 0.587 * src[i + 1] + 0.114 * src[i + 2]
    src[i] = clamp(gray + (src[i] - gray) * satBoost)
    src[i + 1] = clamp(gray + (src[i + 1] - gray) * satBoost)
    src[i + 2] = clamp(gray + (src[i + 2] - gray) * satBoost)
  }

  ctx.putImageData(imageData, 0, 0)
}

/**
 * 3x3 ボックスブラー
 */
function boxBlur3x3(
  src: Uint8ClampedArray,
  dst: Uint8ClampedArray,
  width: number,
  height: number
): void {
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let r = 0, g = 0, b = 0, count = 0
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const nx = x + dx
          const ny = y + dy
          if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
            const idx = (ny * width + nx) * 4
            r += src[idx]
            g += src[idx + 1]
            b += src[idx + 2]
            count++
          }
        }
      }
      const idx = (y * width + x) * 4
      dst[idx] = r / count
      dst[idx + 1] = g / count
      dst[idx + 2] = b / count
      dst[idx + 3] = src[idx + 3]
    }
  }
}

function clamp(val: number): number {
  return val < 0 ? 0 : val > 255 ? 255 : Math.round(val)
}

/**
 * ズームレベルに応じたシャープニング強度を返す
 * z が深いほど強めにかける（ボケが大きいため）
 */
function getStrength(z: number, maxzoom: number): number {
  const diff = z - maxzoom
  if (diff <= 0) return 0
  if (diff === 1) return 0.7  // 2倍拡大
  return 1.0                   // 4倍拡大
}

/**
 * PMTilesインスタンスキャッシュ（同じファイルへの重複接続を防ぐ）
 */
const pmtilesCache = new Map<string, PMTiles>()

/**
 * PMTilesから直接タイルデータを取得
 */
async function fetchPmtilesTile(
  pmtilesUrl: string,
  z: number, x: number, y: number
): Promise<ArrayBuffer> {
  if (!pmtilesCache.has(pmtilesUrl)) {
    pmtilesCache.set(pmtilesUrl, new PMTiles(pmtilesUrl))
  }
  const pmtiles = pmtilesCache.get(pmtilesUrl)!
  const tile = await pmtiles.getZxy(z, x, y)
  if (!tile) throw new Error('Tile not found')
  return tile.data
}

/**
 * タイルデータをImageBitmapに変換して超解像処理し、ArrayBufferで返す
 */
async function processSupeRes(
  tileData: ArrayBuffer,
  z: number,
  maxzoom: number,
  ext: string,
  crop: { sx: number; sy: number; sw: number; sh: number } | null
): Promise<ArrayBuffer> {
  const mimeIn = ext === 'png' ? 'image/png' : ext === 'webp' ? 'image/webp' : 'image/jpeg'
  const blob = new Blob([tileData], { type: mimeIn })
  const img = await createImageBitmap(blob)

  const canvas = new OffscreenCanvas(TILE_SIZE, TILE_SIZE)
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas context failed')

  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'

  drawImageWithCrop(ctx, img, crop, TILE_SIZE, TILE_SIZE)

  const strength = getStrength(z, maxzoom)
  if (strength > 0) {
    applySuperResFilter(ctx, TILE_SIZE, TILE_SIZE, strength)
  }

  const mimeType = ext === 'png' ? 'image/png' : ext === 'webp' ? 'image/webp' : 'image/jpeg'
  const resultBlob = await canvas.convertToBlob({ type: mimeType, quality: 0.9 })
  return resultBlob.arrayBuffer()
}

/**
 * superres:// プロトコルを登録
 *
 * XYZ タイル:   superres://{z}/{x}/{y}.jpg?url=https://...&maxzoom=18
 * PMTiles:      superres://{z}/{x}/{y}.png?pmtiles=https://...pmtiles&maxzoom=15
 */
export function registerSuperResProtocol(
  maplibregl: { addProtocol: Function }
): void {
  maplibregl.addProtocol('superres', async (
    params: { url: string },
    abortController: AbortController
  ) => {
    try {
      const raw = params.url.replace('superres://', '')
      const [pathPart, queryString] = raw.split('?')

      // タイル座標と拡張子を抽出
      const tileMatch = pathPart.match(/^(\d+)\/(\d+)\/(\d+)\.(\w+)$/)
      if (!tileMatch) {
        console.error('[SuperRes] Invalid URL format:', params.url)
        return { data: null }
      }

      const z = parseInt(tileMatch[1], 10)
      const x = parseInt(tileMatch[2], 10)
      const y = parseInt(tileMatch[3], 10)
      const ext = tileMatch[4]

      const urlParams = new URLSearchParams(queryString)
      const maxzoom = parseInt(urlParams.get('maxzoom') || '18', 10)

      // ソース判定: PMTiles or XYZ
      const pmtilesBaseUrl = urlParams.get('pmtiles')
      const xyzUrlTemplate = urlParams.get('url')
      const isPmtiles = !!pmtilesBaseUrl

      if (!isPmtiles && !xyzUrlTemplate) {
        console.error('[SuperRes] Missing url or pmtiles parameter')
        return { data: null }
      }

      // --- z <= maxzoom: パススルー ---
      if (z <= maxzoom) {
        if (isPmtiles) {
          const data = await fetchPmtilesTile(pmtilesBaseUrl!, z, x, y)
          return { data }
        } else {
          const tileUrl = decodeURIComponent(xyzUrlTemplate!)
            .replace('{z}', String(z))
            .replace('{x}', String(x))
            .replace('{y}', String(y))
          const response = await fetch(tileUrl, { signal: abortController.signal })
          if (!response.ok) return { data: null }
          return { data: await response.arrayBuffer() }
        }
      }

      // --- z > maxzoom: 超解像処理 ---
      const coords = calculateTileCoords(z, x, y, maxzoom)

      let tileData: ArrayBuffer
      if (isPmtiles) {
        tileData = await fetchPmtilesTile(pmtilesBaseUrl!, coords.tileZ, coords.tileX, coords.tileY)
      } else {
        const tileUrl = decodeURIComponent(xyzUrlTemplate!)
          .replace('{z}', String(coords.tileZ))
          .replace('{x}', String(coords.tileX))
          .replace('{y}', String(coords.tileY))
        const response = await fetch(tileUrl, { signal: abortController.signal })
        if (!response.ok) return { data: null }
        tileData = await response.arrayBuffer()
      }

      const resultData = await processSupeRes(tileData, z, maxzoom, ext, coords.crop)
      return { data: resultData }
    } catch (error) {
      if ((error as Error).name === 'AbortError') return { data: null }
      console.error('[SuperRes] Error:', error)
      return { data: null }
    }
  })
}
